import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HtmlContent } from 'components/rich';

import PdpIcon from './pdpIcon';

/**
 * FAQ of the redesigned PDP: heading column + accordion, first item open.
 * Collapsed answers stay in the server-rendered HTML (the questions and
 * answers also feed the FAQPage JSON-LD), CSS only hides them.
 */
const ProductFaq = ({ caption, items, eyebrow, title, intro }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const idPrefix = useId();

  return (
    <div className="pdp-faq">
      <div className="pdp-faq-head">
        <p className="pdp-eyebrow">{eyebrow ?? 'FAQ'}</p>
        <h2 className="pdp-h2">{title ?? `Questions about ${caption}`}</h2>
        {intro !== false && (
          <p className="pdp-faq-support">
            {intro ?? (
              <>
                Can’t find your answer? Email us at{' '}
                <a href="mailto:help@parabo.press">help@parabo.press</a>.
              </>
            )}
          </p>
        )}
      </div>

      <div className="pdp-faq-list">
        {items.map((item, index) => {
          const open = index === openIndex;
          const answerId = `${idPrefix}-answer-${index}`;

          return (
            <div key={item.caption} className={classNames('pdp-faq-item', { open })}>
              <h3 className="pdp-faq-question">
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={answerId}
                  onClick={() => setOpenIndex(open ? null : index)}
                >
                  {item.caption}
                  <PdpIcon name={open ? 'chevronUp' : 'chevronDown'} size={18} />
                </button>
              </h3>
              <div id={answerId} className="pdp-faq-answer">
                <div>
                  <HtmlContent className="pdp-faq-answer-body" html={item.description} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ProductFaq.propTypes = {
  caption: PropTypes.string,
  // Overrides for pages that keep an established heading and intro instead of the PDP wording.
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  // false drops the intro line.
  intro: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  items: PropTypes.arrayOf(
    PropTypes.shape({ caption: PropTypes.string, description: PropTypes.string }),
  ).isRequired,
};

export default ProductFaq;
